import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-tree-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeModule,
    ButtonModule,
    DialogModule
  ],
  templateUrl: './tree-selector.component.html',
  styleUrls: ['./tree-selector.component.scss']
})
export class TreeSelectorComponent implements OnInit, OnChanges {
  // Inputs
  @Input() title: string = 'Seleccionar';
  @Input() placeholder: string = 'Seleccionar';
  @Input() treeNodes: TreeNode[] = [];
  @Input() selectedLabels: string[] = [];
  @Input() selectionMode: 'single' | 'multiple' = 'multiple';
  @Input() showSwatch: boolean = true;
  @Input() icon: string = 'pi pi-tag';
  
  // Outputs
  @Output() selectionChange = new EventEmitter<string[]>();

  // UI State
  visible: boolean = false;
  selectedKeys: { [key: string]: boolean } = {};

  ngOnInit(): void {
    // Inicialización
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedLabels'] || changes['treeNodes']) {
      this.syncSelectedKeys();
    }
  }

  /**
   * Sincroniza los labels seleccionados con las keys del tree
   */
  private syncSelectedKeys(): void {
    this.selectedKeys = {};
    
    if (this.selectedLabels.length > 0 && this.treeNodes.length > 0) {
      this.selectedLabels.forEach(label => {
        const node = this.findNodeByLabel(this.treeNodes, label);
        if (node && node.key) {
          this.selectedKeys[String(node.key)] = true;
        }
      });
    }
  }

  /**
   * Abre el modal
   */
  open(): void {
    this.syncSelectedKeys();
    this.visible = true;
  }

  /**
   * Cierra el modal sin aplicar cambios
   */
  close(): void {
    this.visible = false;
  }

  /**
   * Aplica la selección y cierra el modal
   */
  apply(): void {
    const labels: string[] = [];
    Object.keys(this.selectedKeys).forEach(key => {
      if (this.selectedKeys[key]) {
        const node = this.findNodeByKey(this.treeNodes, key);
        if (node && node.label) {
          labels.push(node.label);
        }
      }
    });

    this.selectionChange.emit(labels);
    this.close();
  }

  /**
   * Toggle checkbox de un nodo
   */
  toggleNode(node: TreeNode): void {
    const key = String(node.key ?? '');
    if (!key) return;
    
    const newChecked = !this.isChecked(key);
    this.toggleNodeAndChildren(node, newChecked);
  }

  /**
   * Toggle nodo y sus hijos recursivamente
   */
  private toggleNodeAndChildren(node: TreeNode, checked: boolean): void {
    this.selectedKeys[node.key!] = checked;
    if (node.children) {
      node.children.forEach(child => this.toggleNodeAndChildren(child, checked));
    }
  }

  /**
   * Verifica si un nodo está marcado
   */
  isChecked(key: any): boolean {
    const k = String(key ?? '');
    return !!this.selectedKeys[k];
  }

  /**
   * Expande todos los nodos
   */
  expandAll(): void {
    this.setExpanded(this.treeNodes, true);
    this.treeNodes = [...this.treeNodes];
  }

  /**
   * Contrae todos los nodos
   */
  collapseAll(): void {
    this.setExpanded(this.treeNodes, false);
    this.treeNodes = [...this.treeNodes];
  }

  /**
   * Limpia la selección
   */
  clear(): void {
    this.selectedKeys = {};
    this.selectionChange.emit([]);
    this.close();
  }

  /**
   * Configura estado expandido recursivamente
   */
  private setExpanded(nodes: TreeNode[], expanded: boolean): void {
    nodes.forEach((n) => {
      (n as any).expanded = expanded;
      if (n.children && n.children.length) {
        this.setExpanded(n.children, expanded);
      }
    });
  }

  /**
   * Busca un nodo por label
   */
  private findNodeByLabel(nodes: TreeNode[], label: string): TreeNode | null {
    for (const node of nodes) {
      if (node.label === label) return node;
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByLabel(node.children, label);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Busca un nodo por key
   */
  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Obtiene el texto del botón según la selección
   */
  getButtonText(): string {
    if (this.selectedLabels && this.selectedLabels.length > 0) {
      if (this.selectionMode === 'single') {
        return this.selectedLabels[0];
      }
      return `${this.selectedLabels.length} elementos`;
    }
    return this.placeholder;
  }

  /**
   * Obtiene la cantidad de elementos seleccionados
   */
  getSelectedCount(): number {
    return Object.values(this.selectedKeys || {}).filter(v => v).length;
  }
}
